import { effect, inject, Injectable, Injector, type Signal, signal } from '@angular/core';
import Keycloak from 'keycloak-js';
import {
  AdminUserAuthService,
  type AdminUserRole,
  type AuthAdminUserGetDTO,
} from '@cappus/sdk-mecap-backend';
import {
  KEYCLOAK_EVENT_SIGNAL,
  KeycloakEventType,
  type ReadyArgs,
  typeEventArgs,
} from 'keycloak-angular';
import { openDialog } from '@pct-ng/feature-message-material';
import { handleHttpError } from '@pct-ng/core/error-http';
import type { MaybeArray } from '@pct-ts/typing';
import { ensureItemsArray } from '@pct-ts/common';
import type { AuthServiceInterface } from '@szte-griffin/core/auth/ui';

@Injectable({
  providedIn: 'root',
})
export class MecapAuthService implements AuthServiceInterface<AuthAdminUserGetDTO, AdminUserRole> {
  readonly #keycloak = inject(Keycloak);
  readonly #isLoggedIn = signal(false);
  readonly #keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  readonly #adminUserAuthService = Injector.create({
    providers: [AdminUserAuthService],
    parent: inject(Injector),
  }).get(AdminUserAuthService);
  readonly #me = signal<AuthAdminUserGetDTO | null>(null);

  constructor() {
    effect(() => {
      if (this.#isLoggedIn()) {
        this.#getMe();
      }
    });

    effect(() => {
      const keycloakEvent = this.#keycloakSignal();
      if (keycloakEvent.type === KeycloakEventType.Ready) {
        this.#isLoggedIn.set(typeEventArgs<ReadyArgs>(keycloakEvent.args));
      }

      if (keycloakEvent.type === KeycloakEventType.AuthLogout) {
        this.#isLoggedIn.set(false);
        this.#me.set(null);
      }

      if (keycloakEvent.type === KeycloakEventType.AuthSuccess) {
        this.#isLoggedIn.set(true);
      }
    });
  }

  get isLoggedIn(): Signal<boolean> {
    return this.#isLoggedIn.asReadonly();
  }

  get me(): Signal<AuthAdminUserGetDTO | null> {
    return this.#me.asReadonly();
  }

  refreshGetMe(): void {
    this.#getMe();
  }

  #getMe(): void {
    this.#adminUserAuthService.getCurrentAdminUser().subscribe({
      next: response => {
        this.#me.set(response);
      },
      error: handleHttpError(),
    });
  }

  login(): Promise<void> {
    return this.#keycloak.login();
  }

  logout(): Promise<void> {
    return this.#keycloak.logout();
  }

  hasRealmRole(role: MaybeArray<AdminUserRole>): boolean {
    const roles = ensureItemsArray(role);
    return roles.some(r => this.#keycloak.hasRealmRole(r));
  }

  logoutWithConfirmDialog(): void {
    const i18nPrefix = 'COMMON.LOGOUT';
    openDialog({
      title: `${i18nPrefix}.TITLE`,
      message: `${i18nPrefix}.MESSAGE`,
      buttons: {
        submit: {
          show: true,
          label: `${i18nPrefix}.SUBMIT`,
        },
        cancel: { show: true, label: `${i18nPrefix}.CANCEL` },
      },
    }).subscribe(res => (res?.result === 'SUBMIT' ? this.logout() : 0));
  }

  goToAccount(): Promise<void> {
    return this.#keycloak.accountManagement();
  }
}
